import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getProjects } from '../api/projects';
import { useAuth } from '../hooks/useAuth';
import {
  readStoredActiveProject as readStoredDbName,
  writeStoredActiveProject as writeStoredDbName,
} from '../utils/activeProjectStorage';
import {
  getEffectiveProjectDbName,
  getProjectDisplayName,
  getProjectIdFromPath,
  getProjectNameFromSearch,
  isProjectScopedPath,
} from '../utils/projectName';

export const ActiveProjectContext = createContext(null);

const findProjectByDbName = (projects, dbName) => {
  if (!dbName) return null;
  return (
    projects.find(
      (project) =>
        (project.db_name && project.db_name === dbName) || project.name === dbName
    ) || null
  );
};

export const ActiveProjectProvider = ({ children }) => {
  const { isAuthenticated, user_id } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeDbName, setActiveDbNameState] = useState(readStoredDbName);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const previousUserIdRef = useRef(user_id);

  const projectIdFromPath = useMemo(
    () => getProjectIdFromPath(location.pathname),
    [location.pathname]
  );
  const queryProjectDbName = getProjectNameFromSearch(location.search);
  const isScopedRoute = isProjectScopedPath(location.pathname);

  const persistActiveDbName = useCallback((dbName) => {
    const normalized = (dbName || '').trim();
    setActiveDbNameState(normalized);
    writeStoredDbName(normalized);
  }, []);

  const setActiveProject = useCallback(
    (dbName) => {
      persistActiveDbName(dbName);
    },
    [persistActiveDbName]
  );

  const clearActiveProject = useCallback(() => {
    persistActiveDbName('');
  }, [persistActiveDbName]);

  /** Локально обновить поля проекта в списке шапки (без полного refetch). */
  const patchProject = useCallback((projectId, patch) => {
    if (!projectId || !patch) return;
    setProjects((prev) =>
      prev.map((project) =>
        String(project.project_id) === String(projectId)
          ? { ...project, ...patch }
          : project
      )
    );
  }, []);

  // Смена пользователя (вход/выход) сбрасывает выбранный проект: он принадлежит
  // конкретной учётке и не должен «перетекать» в следующую сессию.
  useEffect(() => {
    if (previousUserIdRef.current === user_id) return;
    previousUserIdRef.current = user_id;
    setProjectsLoaded(false);
    persistActiveDbName('');
  }, [user_id, persistActiveDbName]);

  useEffect(() => {
    if (!isAuthenticated) {
      setProjects([]);
      setProjectsLoaded(false);
      return undefined;
    }

    let mounted = true;
    const loadProjects = async () => {
      try {
        setProjectsLoading(true);
        const data = await getProjects();
        if (!mounted) return;
        const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        setProjects(list.filter((project) => project.status !== 'deleted'));
        setProjectsLoaded(true);
      } catch {
        if (mounted) setProjects([]);
      } finally {
        if (mounted) setProjectsLoading(false);
      }
    };

    loadProjects();
    return () => {
      mounted = false;
    };
  }, [isAuthenticated]);

  // Сохранённый проект мог быть удалён или принадлежать другой учётке —
  // сбрасываем, если его нет в загруженном списке (выбор из URL не трогаем,
  // иначе effect ниже вернёт его обратно и получится цикл).
  useEffect(() => {
    if (!projectsLoaded || !activeDbName || queryProjectDbName) return;
    if (findProjectByDbName(projects, activeDbName)) return;
    persistActiveDbName('');
  }, [projectsLoaded, activeDbName, queryProjectDbName, projects, persistActiveDbName]);

  const pathDbName = useMemo(() => {
    if (!projectIdFromPath || projects.length === 0) return '';
    const project = projects.find(
      (item) => String(item.project_id) === String(projectIdFromPath)
    );
    return project?.db_name || project?.name || '';
  }, [projectIdFromPath, projects]);

  const effectiveDbName = useMemo(
    () =>
      getEffectiveProjectDbName({
        search: location.search,
        activeDbName,
        pathDbName,
      }),
    [location.search, activeDbName, pathDbName]
  );

  const selectedProject = useMemo(() => {
    if (effectiveDbName) {
      return findProjectByDbName(projects, effectiveDbName);
    }
    if (projectIdFromPath) {
      return (
        projects.find((project) => String(project.project_id) === String(projectIdFromPath)) ||
        null
      );
    }
    return null;
  }, [effectiveDbName, projectIdFromPath, projects]);

  useEffect(() => {
    if (!queryProjectDbName || queryProjectDbName === activeDbName) return;
    persistActiveDbName(queryProjectDbName);
  }, [queryProjectDbName, activeDbName, persistActiveDbName]);

  useEffect(() => {
    if (!projectIdFromPath || projects.length === 0) return;
    if (!pathDbName || pathDbName === queryProjectDbName) return;

    const params = new URLSearchParams(location.search);
    params.set('db_name', pathDbName);
    params.delete('project_name');
    const nextSearch = params.toString();
    navigate(
      { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' },
      { replace: true }
    );
  }, [
    projectIdFromPath,
    projects.length,
    pathDbName,
    queryProjectDbName,
    location.pathname,
    location.search,
    navigate,
  ]);

  useEffect(() => {
    if (!isAuthenticated || !isScopedRoute) return;
    if (projectIdFromPath) return;
    if (queryProjectDbName) return;
    if (!activeDbName) return;

    const params = new URLSearchParams(location.search);
    params.set('db_name', activeDbName);
    params.delete('project_name');
    const nextSearch = params.toString();
    navigate(
      { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' },
      { replace: true }
    );
  }, [
    isAuthenticated,
    isScopedRoute,
    projectIdFromPath,
    queryProjectDbName,
    activeDbName,
    location.pathname,
    location.search,
    navigate,
  ]);

  const value = useMemo(
    () => ({
      activeDbName,
      effectiveDbName,
      projects,
      projectsLoading,
      selectedProject,
      selectedProjectLabel:
        getProjectDisplayName(selectedProject) || effectiveDbName || 'Все проекты',
      selectedProjectValue: selectedProject?.db_name || effectiveDbName || '',
      selectedProjectStatus: selectedProject?.status || null,
      isScopedRoute,
      projectIdFromPath,
      setActiveProject,
      clearActiveProject,
      patchProject,
    }),
    [
      activeDbName,
      effectiveDbName,
      projects,
      projectsLoading,
      selectedProject,
      isScopedRoute,
      projectIdFromPath,
      setActiveProject,
      clearActiveProject,
      patchProject,
    ]
  );

  return (
    <ActiveProjectContext.Provider value={value}>{children}</ActiveProjectContext.Provider>
  );
};
